import { useMemo, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getProcurementRequest,
  patchProcurementRequestItem,
  deleteProcurementRequest,
  bulkCreateProcurementRequests,
  type ProcurementRequestItemStatus,
  type ProcurementRequestDetail,
} from '../../shared/api/procurementRequests.api'
import { generateProcurementRequest } from '../requests/utils/generateProcurementRequest'
import { SupplierSelectModal } from '../procurement/components/SupplierSelectModal'
import type { BulkSupplierInput } from '../../shared/api/procurementRequests.api'
import { buildManufacturerReportText } from '../procurement/queueActions'
import { toast } from '../../shared/ui/toastStore'
import { BomItemsTable } from './components/BomItemsTable'
import { Spinner } from '../../shared/ui/Spinner'
import { confirm } from '../../shared/ui/confirmStore'

type BomSourceItem = ProcurementRequestDetail['items'][number]

// Позиции для генерации BOM-файла / переотправки. Осиротевшие строки
// (requestItemId=null, заявка удалена) исключаем: ответ к ним не привязать,
// а Excel-roundtrip и bulk-создание требуют валидный requestItemId.
function buildBomItems(items: BomSourceItem[]) {
  return items
    .filter((it): it is BomSourceItem & { requestItemId: string } => it.requestItemId !== null)
    .map((it) => ({
      requestItemId: it.requestItemId,
      clientCode: null,
      requestNumber: it.requestItem?.request.requestNumber ?? null,
      partNumber: it.partNumber,
      brand: it.brand,
      quantity: it.quantity,
      comment: null,
    }))
}

// Единый ключ имени поставщика (как в SupplierSelectModal): trim + lowercase.
const keyOf = (name: string) => name.trim().toLowerCase()

export function ProcurementRequestDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: req, isLoading } = useQuery({
    queryKey: ['procurement-request', id],
    queryFn: () => getProcurementRequest(id!),
    enabled: !!id,
  })

  const patchItemMut = useMutation({
    mutationFn: (params: { itemId: string; status: ProcurementRequestItemStatus }) =>
      patchProcurementRequestItem(id!, params.itemId, { status: params.status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['procurement-request', id] })
      void queryClient.invalidateQueries({ queryKey: ['procurement-requests'] })
      void queryClient.invalidateQueries({ queryKey: ['procurement-requests-stats'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: () => deleteProcurementRequest(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['procurement-requests'] })
      void queryClient.invalidateQueries({ queryKey: ['procurement-requests-stats'] })
      void navigate('/procurement-requests', { replace: true })
    },
  })

  const stats = useMemo(() => {
    if (!req) return { answered: 0, refused: 0, noResponse: 0, pending: 0, deleted: 0, total: 0 }
    // total — исходное число позиций (с осиротевшими). deleted — строки, чья
    // заявка удалена (requestItemId=null): их исключаем из разбивки статусов и
    // из «остатков», ответа по ним уже не ждём.
    const acc = {
      answered: 0,
      refused: 0,
      noResponse: 0,
      pending: 0,
      deleted: 0,
      total: req.items.length,
    }
    for (const i of req.items) {
      if (i.requestItemId === null) {
        acc.deleted++
        // Ответ по удалённой позиции считаем в «ответил / нет позиции»
        // (поставщик дал цену), но в «ждём» — нет: ответа по удалённой
        // позиции мы не ждём.
        if (i.status === 'ANSWERED') acc.answered++
        else if (i.status === 'REFUSED') acc.refused++
        continue
      }
      if (i.status === 'ANSWERED') acc.answered++
      else if (i.status === 'REFUSED') acc.refused++
      else if (i.status === 'NO_RESPONSE') acc.noResponse++
      else acc.pending++
    }
    return acc
  }, [req])

  // Остатки = ждём ответ. Осиротевшие строки (заявка удалена) исключаем —
  // напоминать поставщику по удалённой позиции нечего.
  const remainingItems = useMemo(
    () => (req?.items ?? []).filter((it) => it.status === 'PENDING' && it.requestItemId !== null),
    [req],
  )

  const downloadFull = () => {
    if (!req) return
    void generateProcurementRequest({
      clientCode: null,
      requestNumber: req.fileCode,
      isBatch: true,
      supplierName: req.supplierName,
      fileCode: req.fileCode,
      items: buildBomItems(req.items),
    })
  }

  const [resendOpen, setResendOpen] = useState(false)
  const [remainingOpen, setRemainingOpen] = useState(false)

  // «Отправить другому поставщику»: открываем модалку выбора поставщиков
  // (та же, что в очереди квот). На confirm — для каждого выбранного
  // поставщика создаётся новый ProcurementRequest с теми же позициями,
  // и сразу скачивается отдельный XLSX-файл.
  const handleResendConfirm = async (
    suppliers: BulkSupplierInput[],
    note: string | null,
    manufacturer: string | null,
  ) => {
    if (!req || (suppliers.length === 0 && !manufacturer)) return
    // Осиротевшие строки (заявка удалена) не переотправляем — их requestItemId
    // больше не существует, bulk-создание по ним упадёт.
    const itemIds = req.items.map((it) => it.requestItemId).filter((v): v is string => v !== null)
    const { requests: created, manufacturerReport } = await bulkCreateProcurementRequests({
      requestItemIds: itemIds,
      suppliers,
      manufacturer: manufacturer ? { name: manufacturer } : undefined,
      note: note ?? undefined,
    })
    for (const newReq of created) {
      // BOM производителю — только вошедшие позиции (занятые коллегой исключены).
      const includedIds = new Set(
        newReq.items.map((it) => it.requestItemId).filter((v): v is string => v !== null),
      )
      await generateProcurementRequest({
        clientCode: null,
        requestNumber: newReq.fileCode,
        isBatch: true,
        supplierName: newReq.supplierName,
        fileCode: newReq.fileCode,
        items: buildBomItems(
          req.items.filter((it) => it.requestItemId && includedIds.has(it.requestItemId)),
        ),
      })
    }
    const reportText = buildManufacturerReportText(manufacturerReport)
    if (reportText) toast.info(reportText)
    void queryClient.invalidateQueries({ queryKey: ['procurement-requests'] })
    void queryClient.invalidateQueries({ queryKey: ['procurement-requests-stats'] })
    setResendOpen(false)
  }

  const handleResendNoSupplier = async () => {
    if (!req) return
    await generateProcurementRequest({
      clientCode: null,
      requestNumber: 'ноунейм',
      isBatch: true,
      fileNameOverride: 'ноунейм',
      items: buildBomItems(req.items),
    })
    setResendOpen(false)
  }

  // «Скачать остатки»: модалка выбора поставщиков с предвыбранным владельцем
  // запроса. Владельцу скачивается файл-напоминание с ОРИГИНАЛЬНЫМ fileCode
  // (ответ привяжется к этому же запросу), для остальных выбранных создаются
  // НОВЫЕ запросы только с позициями-остатками — они появятся в списке
  // «Запросы поставщикам» со своими файлами.
  const handleRemainingConfirm = async (
    suppliers: BulkSupplierInput[],
    note: string | null,
    manufacturer: string | null,
  ) => {
    if (!req || (suppliers.length === 0 && !manufacturer) || remainingItems.length === 0) return
    const bomItems = buildBomItems(remainingItems)

    if (suppliers.some((s) => keyOf(s.name) === keyOf(req.supplierName))) {
      // Скачиваем остатки тем же fileCode что оригинальный запрос — иначе
      // парсер при загрузке ответа не найдёт ProcurementRequest. Суффикс
      // «-remaining» оставляем только в имени файла, чтобы закупщик не
      // путал «полный» и «остатки» в папке загрузок.
      await generateProcurementRequest({
        clientCode: null,
        requestNumber: `${req.fileCode}-remaining`,
        isBatch: true,
        supplierName: req.supplierName,
        fileCode: req.fileCode,
        fileNameOverride: `${req.fileCode}-remaining`,
        items: bomItems,
      })
    }

    const others = suppliers.filter((s) => keyOf(s.name) !== keyOf(req.supplierName))
    if (others.length > 0 || manufacturer) {
      const { requests: created, manufacturerReport } = await bulkCreateProcurementRequests({
        requestItemIds: bomItems.map((it) => it.requestItemId),
        suppliers: others.map((s) => ({ name: s.name })),
        manufacturer: manufacturer ? { name: manufacturer } : undefined,
        note: note ?? undefined,
      })
      const remainingReport = buildManufacturerReportText(manufacturerReport)
      if (remainingReport) toast.info(remainingReport)
      for (const newReq of created) {
        // BOM производителю — только вошедшие позиции (занятые исключены).
        const includedIds = new Set(
          newReq.items.map((it) => it.requestItemId).filter((v): v is string => v !== null),
        )
        await generateProcurementRequest({
          clientCode: null,
          requestNumber: newReq.fileCode,
          isBatch: true,
          supplierName: newReq.supplierName,
          fileCode: newReq.fileCode,
          items: bomItems.filter((it) => includedIds.has(it.requestItemId)),
        })
      }
      void queryClient.invalidateQueries({ queryKey: ['procurement-requests'] })
      void queryClient.invalidateQueries({ queryKey: ['procurement-requests-stats'] })
    }
    setRemainingOpen(false)
  }

  const handleRemainingNoSupplier = async () => {
    if (!req || remainingItems.length === 0) return
    await generateProcurementRequest({
      clientCode: null,
      requestNumber: 'ноунейм',
      isBatch: true,
      fileNameOverride: 'ноунейм',
      items: buildBomItems(remainingItems),
    })
    setRemainingOpen(false)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    )
  }
  if (!req) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <p className="text-sm text-gray-500">Запрос не найден.</p>
        <Link to="/procurement-requests" className="text-brand-600 hover:underline text-sm">
          ← К списку запросов
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/procurement-requests" className="text-gray-400 hover:text-gray-600 text-sm">
          ← Запросы
        </Link>
        <h1 className="text-xl font-semibold text-gray-900 font-mono">{req.fileCode}</h1>
        <span className="text-sm text-gray-500">·</span>
        <span className="text-base font-medium text-gray-800">{req.supplierName}</span>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
          <div>
            <span className="block text-xs text-gray-500">Создан</span>
            <span className="text-gray-800">
              {new Date(req.createdAt).toLocaleString('ru-RU', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
          <div>
            <span className="block text-xs text-gray-500">Закупщик</span>
            <span className="text-gray-800">
              {req.procurementUser.name ?? req.procurementUser.email}
            </span>
          </div>
          <div>
            <span className="block text-xs text-gray-500">Позиций</span>
            {stats.deleted > 0 ? (
              <span
                className="text-gray-800 font-medium"
                title="Часть позиций удалена вместе с заявкой"
              >
                {stats.total - stats.deleted} из {stats.total}{' '}
                <span className="text-red-600 font-normal">(−{stats.deleted} удалены)</span>
              </span>
            ) : (
              <span className="text-gray-800 font-medium">{stats.total}</span>
            )}
          </div>
          <div>
            <span className="block text-xs text-gray-500">Ответил / Ждём</span>
            <span className="text-gray-800">
              <span className="text-emerald-600 font-medium">{stats.answered}</span> /{' '}
              <span className="text-blue-600 font-medium">{stats.pending}</span>
            </span>
          </div>
          <div>
            <span className="block text-xs text-gray-500">Заметка</span>
            <span className="text-gray-700 text-xs">{req.note || '—'}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <button
            onClick={downloadFull}
            className="px-3 py-1.5 text-sm font-medium bg-gray-800 text-white rounded-lg hover:bg-gray-900"
          >
            Скачать полный файл
          </button>
          <button
            onClick={() => setRemainingOpen(true)}
            disabled={stats.pending === 0}
            title="BOM только с позициями, по которым ждём ответ: текущему поставщику — файл-напоминание, другим выбранным — новые запросы"
            className="px-3 py-1.5 text-sm font-medium border border-amber-300 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 disabled:opacity-50"
          >
            Скачать остатки ({stats.pending})
          </button>
          <button
            onClick={() => setResendOpen(true)}
            title="Отправить этот же набор позиций другому поставщику — создастся новый запрос и скачается отдельный файл"
            className="px-3 py-1.5 text-sm font-medium border border-brand-300 bg-brand-50 text-brand-700 rounded-lg hover:bg-brand-100"
          >
            Отправить другому поставщику
          </button>
          <div className="flex-1" />
          <button
            onClick={async () => {
              if (
                await confirm({
                  title: `Удалить запрос ${req.fileCode}?`,
                  danger: true,
                  confirmText: 'Удалить',
                })
              )
                deleteMut.mutate()
            }}
            disabled={deleteMut.isPending}
            className="px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
          >
            Удалить
          </button>
        </div>
      </div>

      {resendOpen && (
        <SupplierSelectModal
          itemsCount={req.items.length}
          onClose={() => setResendOpen(false)}
          onConfirm={handleResendConfirm}
          onDownloadNoSupplier={handleResendNoSupplier}
        />
      )}

      {remainingOpen && (
        <SupplierSelectModal
          itemsCount={remainingItems.length}
          initialSuppliers={[req.supplierName]}
          onClose={() => setRemainingOpen(false)}
          onConfirm={handleRemainingConfirm}
          onDownloadNoSupplier={handleRemainingNoSupplier}
        />
      )}

      <BomItemsTable
        items={req.items}
        onPatchStatus={(itemId, status) => patchItemMut.mutate({ itemId, status })}
      />
    </div>
  )
}
